'use client';

import { useTranslations } from 'next-intl';
import React, { Dispatch, SetStateAction, useMemo, useRef } from 'react';
import ReactPlayer from 'react-player';
import { useOrientation, useWindowSize } from 'react-use';

import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';
import { setIframeTitle } from '@cineamo/ui/src/helpers/set-iframe-title';

import FullScreenModal from '@cineamo/ui/src/modals/FullScreenModal';

type VideoPlayerModalProps = {
    url: string;
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({ url, isOpen, setIsOpen }) => {
    const t = useTranslations();
    const isMobile = useIsMobile('lg');
    const orientation = useOrientation();
    const { width, height } = useWindowSize();
    const videoPlayerRef = useRef(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);

    const playerSize = useMemo(() => {
        const ratio = 540 / 960;
        // 100 pixels added for player controls
        const playerHeightOnMobile = width * ratio + 100;
        return {
            width: isMobile ? width : 960,
            height: isMobile ? (orientation.type === 'landscape-primary' ? height : playerHeightOnMobile) : 540
        };
    }, [orientation.type, isMobile, width, height]);

    return (
        <>
            {isOpen && (
                <FullScreenModal
                    modalIsOpen={isOpen}
                    setModalIsOpen={setIsOpen}
                    onClose={null}>
                    <div ref={playerContainerRef}>
                        <ReactPlayer
                            url={url}
                            onReady={() => setIframeTitle(playerContainerRef.current, t('common.video'))}
                            playing={isOpen}
                            onEnded={() => null}
                            controls={true}
                            ref={videoPlayerRef}
                            width={playerSize.width}
                            height={playerSize.height}
                        />
                    </div>
                </FullScreenModal>
            )}
        </>
    );
};

export default VideoPlayerModal;
